// @subframe/sync-disable
"use client";
/*
 * Documentation:
 * Icon Button — https://app.subframe.com/06b570ce924b/library?component=Icon+Button_af9405b1-8c54-4e01-9786-5aad308224f6
 * Progress — https://app.subframe.com/06b570ce924b/library?component=Progress_60964db0-a1bf-428b-b9d5-f34cdf58ea77
 * topBar — https://app.subframe.com/06b570ce924b/library?component=topBar_a952092b-4b61-4507-a10d-01895e80244e
 */

import React from "react";
import { FeatherClock } from "@subframe/core";
import { FeatherMenu } from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";
import { IconButton } from "./IconButton";
import { Progress } from "./Progress";

interface TopBarRootProps extends React.HTMLAttributes<HTMLDivElement> {
  menuIcon?: React.ReactNode;
  userName?: React.ReactNode;
  userMenu?: React.ReactNode;
  header?: React.ReactNode;
  showWifi?: boolean;
  rightFirst?: React.ReactNode;
  rightSecond?: React.ReactNode;
  rightThird?: React.ReactNode;
  variant?: "primary-pages" | "inner-pages";
  count?: React.ReactNode;
  progress?: number;
  dueLabel?: string;
  onMenuClick?: () => void;
  className?: string;
}

const TopBarRoot = React.forwardRef<HTMLDivElement, TopBarRootProps>(
  function TopBarRoot(
    {
      menuIcon = <FeatherMenu />,
      userName,
      userMenu,
      header,
      showWifi = false,
      rightFirst,
      rightSecond,
      rightThird,
      variant = "primary-pages",
      count,
      progress = 50,
      dueLabel,
      onMenuClick,
      className,
      ...otherProps
    }: TopBarRootProps,
    ref,
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/a952092b flex w-full flex-col items-center gap-2 border-b border-solid border-secondary-beige-200 bg-default-background px-6 py-3 mobile:px-4 mobile:py-4",
          {
            "flex-col flex-nowrap gap-4 mobile:flex-col mobile:flex-nowrap mobile:gap-1 mobile:px-4 mobile:py-4":
              variant === "inner-pages",
          },
          className,
        )}
        ref={ref}
        {...otherProps}
      >
        <div className="flex w-full items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 pt-1 pb-1.5 mobile:flex-row mobile:flex-nowrap mobile:items-center mobile:justify-start mobile:gap-2">
            <div className="flex items-center gap-2">
              {menuIcon ? (
                <SubframeCore.IconWrapper className="hidden text-large-button font-large-button text-default-font mobile:text-metrics mobile:font-metrics">
                  {menuIcon}
                </SubframeCore.IconWrapper>
              ) : null}
              <IconButton
                className="mobile:h-11 mobile:w-11 mobile:flex-none"
                aria-label="Toggle navigation"
                disabled={false}
                size="large"
                icon={menuIcon}
                loading={false}
                onClick={onMenuClick}
              />
            </div>
            {header ? (
              <span className="hidden w-full text-heading-2 font-heading-2 text-default-font mobile:inline mobile:text-extra-large-input mobile:font-extra-large-input mobile:text-primary-navy-800">
                {header}
              </span>
            ) : null}
          </div>
          <div
            className={SubframeUtils.twClassNames(
              "flex items-center gap-4 mobile:items-center mobile:justify-end",
              { "mobile:items-center mobile:justify-end": showWifi },
            )}
          >
            {rightThird ? (
              <div
                className={SubframeUtils.twClassNames(
                  "hidden items-center gap-4 mobile:items-center mobile:justify-end",
                  {
                    "mobile:flex": variant === "inner-pages",
                    "mobile:flex mobile:items-center mobile:justify-end":
                      showWifi,
                  },
                )}
              >
                {rightThird}
              </div>
            ) : null}
            <SubframeCore.DropdownMenu.Root>
              <SubframeCore.DropdownMenu.Trigger asChild={true}>
                <div
                  className={SubframeUtils.twClassNames(
                    "flex items-center gap-3",
                    {
                      "mobile:hidden mobile:items-center mobile:justify-end":
                        showWifi && variant !== "inner-pages",
                    },
                  )}
                >
                  {rightSecond ? (
                    <div
                      className={SubframeUtils.twClassNames(
                        "flex items-center gap-3 cursor-pointer",
                        {
                          "mobile:hidden": showWifi,
                        },
                      )}
                    >
                      {rightSecond}
                    </div>
                  ) : null}
                  {rightFirst ? (
                    <div className="flex items-center gap-3 cursor-pointer">
                      {rightFirst}
                    </div>
                  ) : null}
                </div>
              </SubframeCore.DropdownMenu.Trigger>
              <SubframeCore.DropdownMenu.Portal>
                <SubframeCore.DropdownMenu.Content
                  side="bottom"
                  align="end"
                  sideOffset={4}
                  asChild={true}
                  className="z-[100]"
                >
                  {userMenu ? (
                    <div className="flex items-start justify-between z-[100]">
                      {userMenu}
                    </div>
                  ) : null}
                </SubframeCore.DropdownMenu.Content>
              </SubframeCore.DropdownMenu.Portal>
            </SubframeCore.DropdownMenu.Root>
          </div>
        </div>
        <div
          className={SubframeUtils.twClassNames(
            "hidden w-full flex-col items-center gap-1",
            { "flex mobile:flex": variant === "inner-pages" },
          )}
        >
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-1 mobile:flex-row mobile:flex-nowrap mobile:gap-2">
              <FeatherClock className="text-caption font-caption text-subtext-color" />
              <span className="text-caption font-caption text-subtext-color">
                {dueLabel ? `Due: ${dueLabel}` : "Due: —"}
              </span>
            </div>
          </div>
          <div className="flex w-full items-center gap-2">
            <Progress value={progress} />
            {count ? (
              <span className="text-caption font-caption text-subtext-color whitespace-nowrap">
                {count}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    );
  },
);

export const TopBar = TopBarRoot;
